import { Loop } from './core/Loop';
import { createInitialState } from './sim/AircraftState';
import { FlightModel } from './sim/FlightModel';
import { Instruments } from './systems/Instruments';
import { computeILS } from './systems/Navigation';
import { Controls } from './systems/Controls';
import { Keyboard } from './input/Keyboard';
import { Gamepad } from './input/Gamepad';
import { JoystickConfigUI } from './ui/JoystickConfigUI';
import { Scene } from './render/Scene';
import { Panel } from './panel/Panel';
import { AudioEngine } from './audio/AudioEngine';
import { makeWeather } from './weather/Weather';
import { windFromMet } from './sim/Atmosphere';
import { RUNWAY_24, WORLD, approachStartFor, finalApproachStartFor, StartPose } from './world/Airport';
import { computeFitRect, applyFitRect } from './core/Letterbox';

const sceneCanvas = document.getElementById('scene') as HTMLCanvasElement;
const panelCanvas = document.getElementById('panel') as HTMLCanvasElement;
const hud = document.getElementById('hud') as HTMLDivElement;
const overlay = document.getElementById('startOverlay') as HTMLDivElement;
const startBtn = document.getElementById('startBtn') as HTMLButtonElement;
const soundBtn = document.getElementById('soundBtn') as HTMLButtonElement;
const startPosSelect = document.getElementById('startPosSelect') as HTMLSelectElement;
const windPanel = document.getElementById('windPanel') as HTMLDivElement;
const windDirSlider = document.getElementById('windDirSlider') as HTMLInputElement;
const windSpdSlider = document.getElementById('windSpdSlider') as HTMLInputElement;
const windDirVal = document.getElementById('windDirVal') as HTMLSpanElement;
const windSpdVal = document.getElementById('windSpdVal') as HTMLSpanElement;

const KTS_TO_MS = 0.514444;
const FT_TO_M = 0.3048;

// --- Simulation objects ----------------------------------------------------
const state = createInitialState();
const model = new FlightModel();
const instruments = new Instruments();
const controls = new Controls();
// Render every airport's runway; the flat ground sits at the active airport's
// elevation. model.terrain is the active airport's surface model (the seam a
// future elevation model plugs into).
const scene = new Scene(
  sceneCanvas,
  WORLD.airports.flatMap((a) => a.runways),
  WORLD.active.terrain.elevationAt(0, 0),
);
model.terrain = WORLD.active.terrain;
const panel = new Panel(panelCanvas);
let weather = makeWeather(2); // CAVOK (perfect visibility) to start; press 1/2/3 for IMC

let audio: AudioEngine | null = null;

// --- Pause (P key): freezes the physics step; rendering/HUD keep running ---
// Starts true: the sim is already rendering (frozen) behind the start dialog
// — see "Start dialog" below — rather than a blank screen until dismissed.
let paused = true;
function setPaused(v: boolean): void {
  paused = v;
  audio?.setSilenced(v); // mute while paused, independent of the user's own sound preference
}

// Selectable starting positions. Each preset names the airport it belongs to
// (so selecting it switches the active airport — nav target, terrain, ground
// plane) and a pose. KAPC runway 24 keeps the original cruise/intercept start
// plus three final-approach presets; KSTS runway 32 demonstrates the second
// field. Heights are ABOVE the runway; distance-out follows from the GS angle.
const KSTS_RWY = WORLD.airport('KSTS')!.runways[0];
interface StartPreset { airport: string; label: string; pose: StartPose; }
const START_PRESETS: Record<string, StartPreset> = {
  cruise: { airport: 'KAPC', label: 'KAPC 24 — Cruise 1000 ft, 4 NM final', pose: approachStartFor(RUNWAY_24, 4, 1000) },
  final500: { airport: 'KAPC', label: 'KAPC 24 — 500 ft final', pose: finalApproachStartFor(RUNWAY_24, 500) },
  final1000: { airport: 'KAPC', label: 'KAPC 24 — 1000 ft final', pose: finalApproachStartFor(RUNWAY_24, 1000) },
  final3000: { airport: 'KAPC', label: 'KAPC 24 — 3000 ft final', pose: finalApproachStartFor(RUNWAY_24, 3000) },
  sts_cruise: { airport: 'KSTS', label: 'KSTS 32 — Cruise 1000 ft, 4 NM final', pose: approachStartFor(KSTS_RWY, 4, 1000) },
  sts_final1000: { airport: 'KSTS', label: 'KSTS 32 — 1000 ft final', pose: finalApproachStartFor(KSTS_RWY, 1000) },
};
type StartPresetKey = keyof typeof START_PRESETS;
let currentStartPreset: StartPresetKey = 'cruise';

// Populate the start-position dropdown from the presets above (keeps the option
// list and the labels in one place rather than duplicated in index.html).
startPosSelect.innerHTML = '';
for (const [key, p] of Object.entries(START_PRESETS)) {
  const opt = document.createElement('option');
  opt.value = key;
  opt.textContent = p.label;
  startPosSelect.appendChild(opt);
}

/**
 * Reset to the in-flight start position currently selected (default: 1000 ft,
 * 4 NM out on the runway-24 approach course), clean config, roughly trimmed
 * for level flight.
 */
function resetInFlight(): void {
  const preset = START_PRESETS[currentStartPreset];
  // Switch the active airport if this preset belongs to a different one — this
  // repoints the ILS nav target, the flight-model terrain, and the render
  // ground-plane elevation. (A ground start-pose at a non-zero-elevation field
  // would derive pos.d from terrain + gear height; all current presets are
  // in-flight, so pose.altitudeFt is the MSL height directly.)
  if (WORLD.activeIdent !== preset.airport) {
    WORLD.activeIdent = preset.airport;
    model.terrain = WORLD.active.terrain;
    scene.setGroundElevation(WORLD.active.terrain.elevationAt(0, 0));
  }
  const pose = preset.pose;
  const s = createInitialState();
  s.pos.n = pose.n;
  s.pos.e = pose.e;
  s.pos.d = -(pose.altitudeFt * FT_TO_M);
  s.psi = (pose.headingDeg * Math.PI) / 180;

  const V = pose.speedKts * KTS_TO_MS;
  s.vel.n = V * Math.cos(s.psi);
  s.vel.e = V * Math.sin(s.psi);
  s.vel.d = 0;
  s.theta = 0.021; // ~1.2° deck angle for level flight at this speed
  s.rpm = 1800; // already spun up to a cruise setting
  s.onGround = false;
  Object.assign(state, s);

  controls.throttle = 0.65; // ~level cruise power at 1000 ft
  controls.flapDetent = 0; // clean
  controls.trim = -0.05; // roughly trimmed for level
  controls.mixture = 0.9;
  controls.brakes = 0;
  controls.parkingBrake = false;
  controls.gearDown = true;

  // Seed the derived readout fields (tas, altitude, alpha, verticalSpeed, ...)
  // that FlightModel.step() normally computes each physics tick — with a
  // zero dt this only computes them from the pose just set above, without
  // advancing anything. Needed because the sim now starts paused (rendering
  // behind the start dialog) and the physics step wouldn't otherwise run even
  // once before the HUD/instruments first read these fields.
  model.step(state, controls.snapshot(), 0);
}
resetInFlight();

/** Shift+R: full reset back to page-load defaults (pose, weather, pause state). */
function hardReset(): void {
  currentStartPreset = 'cruise';
  startPosSelect.value = currentStartPreset;
  resetInFlight();
  weather = makeWeather(2); // back to CAVOK
  setPaused(false);
  mouseControlActive = false;
}

// --- Start position picker --------------------------------------------------
startPosSelect.value = currentStartPreset;
startPosSelect.addEventListener('change', () => {
  currentStartPreset = startPosSelect.value as StartPresetKey;
  resetInFlight();
  // Otherwise the dropdown keeps keyboard focus after picking an option, and
  // the next key the pilot presses (e.g. S for pitch) gets consumed by the
  // <select> (jumping to another option) instead of reaching flight controls.
  startPosSelect.blur();
});

// --- Wind (top-right sliders): direction it's coming FROM (met convention,
// degrees true) and speed in knots, 0..60. Applied directly to the flight
// model's steady wind field used for relative-airspeed/wind-correction.
function applyWind(): void {
  const dir = parseInt(windDirSlider.value, 10);
  const spd = parseInt(windSpdSlider.value, 10);
  windDirVal.textContent = `${dir.toString().padStart(3, '0')}°`;
  windSpdVal.textContent = `${spd} kt`;
  model.wind = windFromMet(dir, spd);
}
windDirSlider.addEventListener('input', applyWind);
windSpdSlider.addEventListener('input', applyWind);
applyWind();

// --- Input -----------------------------------------------------------------
const keyboard = new Keyboard(controls);
const gamepad = new Gamepad(controls);
const joyConfig = new JoystickConfigUI(gamepad);
keyboard.onReset = resetInFlight;
keyboard.onHardReset = hardReset;
keyboard.onVisibility = (p) => { weather = makeWeather(p); };
keyboard.onTogglePause = () => { setPaused(!paused); };

// --- Mouse XY flight control -------------------------------------------------
// Double-click the outside view (horizon) to engage: cursor position relative
// to the centre of a SQUARE control region (side length = the outside view's
// height, so the full range is reachable without ever moving the mouse down
// onto the instrument panel) drives aileron (X) and elevator (Y), like a
// virtual yoke. Once engaged, a second double-click ANYWHERE releases it back
// to keyboard/joystick control — the disengage gesture shouldn't depend on
// where the cursor happens to be.
//
// The panel canvas is transparent above the glareshield but still sits on top
// (z-index 1) and covers the whole fit rect, so it — not the scene canvas —
// is what actually receives pointer events; the listener has to live there.
let mouseControlActive = false;
let mouseAileron = 0;
let mouseElevator = 0;
panelCanvas.addEventListener('dblclick', (e) => {
  if (mouseControlActive) {
    // Disengage always works, regardless of cursor position.
    mouseControlActive = false;
    mouseAileron = 0; mouseElevator = 0;
    panelCanvas.style.cursor = '';
    return;
  }
  // Engaging still requires double-clicking the horizon specifically, so it
  // doesn't trigger while working with the panel/radios.
  const rect = panelCanvas.getBoundingClientRect();
  const dashTop = rect.top + rect.height * panel.glareshieldFrac;
  if (e.clientY >= dashTop) return; // click was on the panel, not the horizon
  mouseControlActive = true;
  panelCanvas.style.cursor = 'crosshair';
});
window.addEventListener('mousemove', (e) => {
  if (!mouseControlActive) return;
  const rect = sceneCanvas.getBoundingClientRect();
  // Square control region centred in the outside view (sky/ground band above
  // the glareshield): its side length equals that band's height, so full
  // deflection is reached without the mouse ever needing to leave it.
  const outsideViewH = rect.height * panel.glareshieldFrac;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + outsideViewH / 2;
  const half = outsideViewH / 2;
  mouseAileron = clamp((e.clientX - cx) / half, -1, 1);
  // Reported reversed in testing — flipped the sign (was -(...), matching
  // the trim-key fix earlier: same lesson, trust the live-tested direction.
  mouseElevator = clamp((e.clientY - cy) / half, -1, 1); // mouse up = nose down
});
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

// --- Fixed 16:9 letterboxed layout ------------------------------------------
// Everything (outside view, panel, HUD, sound button) is rendered at one
// canonical 16:9 aspect ratio, scaled as large as fits the browser window and
// centred — black bars fill the rest. This is what actually fixes the panel
// ever clipping: there is only ever one aspect ratio to lay gauges out for.
function applyLayout(): void {
  const rect = computeFitRect(window.innerWidth, window.innerHeight);
  applyFitRect(sceneCanvas, rect);
  applyFitRect(panelCanvas, rect);
  scene.resize(rect);
  panel.resize(rect);

  // Anchor the HUD/sound button to the fit rect's corners, not the window's —
  // otherwise they'd sit in the letterbox bars on a mismatched window shape.
  hud.style.left = `${rect.x + 8}px`;
  hud.style.top = `${rect.y + 8}px`;
  soundBtn.style.right = `${window.innerWidth - (rect.x + rect.width) + 8}px`;
  soundBtn.style.top = `${rect.y + 8}px`;
  windPanel.style.right = `${window.innerWidth - (rect.x + rect.width) + 8}px`;
  windPanel.style.top = `${rect.y + 56}px`; // just below the sound button
  startPosSelect.style.left = `${rect.x + rect.width / 2}px`;
  startPosSelect.style.top = `${rect.y + 8}px`;
  startPosSelect.style.transform = 'translateX(-50%)';
}
window.addEventListener('resize', applyLayout);
// Resize on the browser's own 'fullscreenchange' event rather than right after
// requestFullscreen()'s promise resolves: that promise can settle before the
// viewport has finished resizing, which would otherwise use stale dimensions.
document.addEventListener('fullscreenchange', applyLayout);
applyLayout();

// --- Fullscreen (Ctrl+F) and joystick config (Ctrl+J) -----------------------
async function toggleFullscreen(): Promise<void> {
  try {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
    } else {
      await document.exitFullscreen();
    }
  } catch {
    /* fullscreen may be blocked by the browser; ignore */
  }
}
window.addEventListener('keydown', (e) => {
  const k = e.key.toLowerCase();
  if (e.ctrlKey && k === 'f') {
    e.preventDefault();
    void toggleFullscreen();
  } else if (e.ctrlKey && k === 'j') {
    e.preventDefault();
    joyConfig.toggle();
  } else if (e.ctrlKey && k === 'r') {
    // Without this, Ctrl+R falls through to the browser's own page reload —
    // wiping the whole app (start screen, sim state) instead of just
    // repositioning at the selected start point like the plain R key does.
    e.preventDefault();
    resetInFlight();
  }
});

// --- Sound ON/OFF button (replaces the audio hotkeys); preference persists ---
const SOUND_PREF_KEY = 'ifrtrainer.soundOn.v1';
function loadSoundPref(): boolean {
  try {
    const v = localStorage.getItem(SOUND_PREF_KEY);
    return v === null ? true : v === '1';
  } catch {
    return true; // storage unavailable (e.g. private browsing) — default on
  }
}
function saveSoundPref(on: boolean): void {
  try { localStorage.setItem(SOUND_PREF_KEY, on ? '1' : '0'); } catch { /* ignore */ }
}
function updateSoundBtn(): void {
  const on = !audio?.muted;
  soundBtn.textContent = on ? '🔊' : '🔇'; // struck-through speaker when muted
  soundBtn.classList.toggle('off', !on);
}
soundBtn.addEventListener('click', () => {
  audio?.toggleMute();
  saveSoundPref(!audio?.muted);
  updateSoundBtn();
});

// --- Stall warning tracking ------------------------------------------------
let stallActive = false;

// Whether the gamepad actually drove input THIS frame (not just whether one is
// connected), so the HUD is honest even with an idle phantom device present.
let gamepadActive = false;

// --- Main loop -------------------------------------------------------------
let lastNav = computeILS(state, WORLD.activeRunway);

const loop = new Loop(
  (dt) => {
    // Input: joystick overrides keyboard axes only when actually engaged.
    const padUsed = gamepad.poll();
    keyboard.poll(dt);
    if (padUsed) {
      // gamepad.poll already wrote targets; keyboard.poll may have zeroed them
      // for un-pressed keys, so re-poll gamepad after keyboard to win.
      gamepad.poll();
    }
    gamepadActive = padUsed;

    // Mouse XY control (toggled by double-clicking the horizon) takes final
    // priority over keyboard/joystick while active, same "last writer wins"
    // pattern as the gamepad-after-keyboard re-poll above.
    if (mouseControlActive) {
      controls.aileronTarget = mouseAileron;
      controls.elevatorTarget = mouseElevator;
    }
    controls.update(dt);

    if (paused) return; // freeze the flight model; input/HUD keep working

    model.step(state, controls.snapshot(), dt);

    // Stall warning: near/after critical AoA with airspeed alive.
    const stalling = state.alpha > 0.26 && state.tas > 5;
    if (stalling !== stallActive) {
      stallActive = stalling;
      audio?.setStallWarning(stalling);
    }
  },
  () => {
    const r = instruments.update(state, 1 / 60);
    lastNav = computeILS(state, WORLD.activeRunway);

    scene.update(state, weather);
    scene.render();

    panel.load(r);
    panel.draw(lastNav, controls.snapshot());

    audio?.updateEngine(state.rpm);

    const gpuMs = scene.gpuMs;
    const gpuStr = gpuMs === null ? 'n/a' : `${((gpuMs / (1000 / 60)) * 100).toFixed(0)}%`;
    hud.textContent =
      `IAS ${r.ias.toFixed(0).padStart(3)} kt   ALT ${r.altitude.toFixed(0)} ft   ` +
      `VS ${r.vsi.toFixed(0)} fpm   HDG ${r.headingDeg.toFixed(0).padStart(3)}°\n` +
      `RPM ${r.rpm.toFixed(0)}   FUEL ${r.fuelL.toFixed(0)} L   ` +
      `${mouseControlActive ? 'MOUSE' : gamepadActive ? 'JOYSTICK' : 'KEYBOARD'}   ${loop.fps.toFixed(0)} fps   ` +
      `CPU ${loop.cpuLoadPct.toFixed(0)}%   GPU ${gpuStr}` +
      (state.onGround ? '   [ON GROUND]' : '') +
      (stallActive ? '   *** STALL ***' : '') +
      (paused ? '   [PAUSED — P to resume]' : '');
  },
  100,
);

// The sim renders immediately (frozen — see `paused` above) so it's visible
// behind the start dialog rather than a blank screen.
loop.start();

// --- Start dialog -------------------------------------------------------
// A dismissible dialog, not a blocking loading screen: clicking the button OR
// clicking the backdrop outside the card unpauses and (on first dismissal)
// creates the AudioEngine — deferred until here since it needs a user gesture.
function startSim(): void {
  overlay.style.display = 'none';
  soundBtn.style.display = 'flex';
  windPanel.style.display = 'block';
  if (!audio) {
    audio = new AudioEngine();
    audio.resume();
    if (!loadSoundPref()) audio.toggleMute();
    updateSoundBtn();
    audio.beep(1200, 0.12, 0.2); // "ready" chime
  }
  setPaused(false);
}
startBtn.addEventListener('click', startSim);
overlay.addEventListener('click', (e) => {
  if (e.target === overlay) startSim(); // click landed on the backdrop, not the card
});
